/**
 * Phase 7-lite — Login Stats dashboard.
 *
 * Per-customer login telemetry: how many ever logged in, PWA vs browser
 * adoption, who's active in last N days, who's never opened the app.
 *
 * Pairs with the new "Has signed in" filter on Founder's Thanks — use this
 * page to identify dormant/non-onboarded customers, then send them a
 * bulk re-engagement message.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { CustomerTierDistribution } from '../../components/CustomerTierDistribution';
import {
  ArrowLeft, RefreshCw, Users, Smartphone, Globe, Clock, Send,
} from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const fmtAgo = (iso) => {
  if (!iso) return 'never';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return `${Math.floor(diff / 604800)}w`;
  } catch { return iso; }
};

const Stat = ({ label, value, sub, icon: Icon, color = '#39FF14' }) => (
  <div
    className="border border-[#39FF14]/20 bg-[#0C0612] p-4"
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-2 mb-1">
      {Icon ? <Icon className="w-3.5 h-3.5" style={{ color }} /> : null}
      <div className="font-mono text-[10px] text-[#4B6B52] tracking-widest uppercase">
        {label}
      </div>
    </div>
    <div className="font-mono text-3xl font-bold" style={{ color }}>{value}</div>
    {sub ? <div className="font-mono text-[11px] text-[#A3C7A8] mt-0.5">{sub}</div> : null}
  </div>
);

export default function LoginStatsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [filter, setFilter] = useState('all'); // all | active | dormant | never | pwa | browser
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/login-stats?days=${days}`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load login stats');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rows = (data?.rows || []).filter((r) => {
    if (filter === 'never' && r.login_count > 0) return false;
    if (filter === 'active' && !r.active_recent) return false;
    if (filter === 'dormant' && (r.login_count === 0 || r.active_recent)) return false;
    // PWA filter: include both explicit standalone logins AND fallback-rescued
    // (push subscription / install bonus). `any_standalone` is true for both
    // after the Feb 2026 backend rescue logic.
    if (filter === 'pwa' && !r.any_standalone) return false;
    // BROWSER filter: only explicit browser-only users (must have logged in
    // AND not be PWA AND not awaiting classification). Excludes ghosts.
    if (filter === 'browser' && (r.login_count === 0 || r.any_standalone || r.awaiting_classification)) return false;
    // AWAITING filter: customers whose original login predates is_standalone
    // telemetry and we have no fallback signal (drains as heartbeat fires).
    if (filter === 'awaiting' && !r.awaiting_classification) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = `${r.email || ''} ${r.name || ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  const total = data?.total || 0;
  const pwaPct = total > 0 ? Math.round((data?.pwa_users || 0) * 100 / total) : 0;
  const everPct = total > 0 ? Math.round((data?.ever_logged_in || 0) * 100 / total) : 0;

  return (
    <div className="min-h-screen bg-[#050003] text-white dispatch-safe-top" data-testid="login-stats-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dispatch')}
              className="text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none"
              data-testid="back-to-dispatch"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> BACK
            </Button>
            <div>
              <h1 className="text-[#39FF14] font-mono text-2xl font-bold flex items-center gap-2">
                <Users className="w-6 h-6" />
                LOGIN_STATS
              </h1>
              <p className="text-[#4B6B52] font-mono text-sm">
                {'// per-customer login telemetry · pwa vs browser adoption'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 border border-[#39FF14]/30 rounded-none p-1">
              {[7, 30, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  className={`px-2 py-1 font-mono text-xs ${
                    days === n
                      ? 'bg-[#39FF14] text-[#050003] font-bold'
                      : 'text-[#A3C7A8] hover:text-[#39FF14]'
                  }`}
                  data-testid={`days-${n}`}
                >
                  {n}D
                </button>
              ))}
            </div>
            <Button
              onClick={fetchAll}
              disabled={loading}
              variant="outline"
              className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none font-mono"
              data-testid="refresh-stats"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
          </div>
        </div>

        {/* Top-line stats — Phase 6.1.1: renamed "Never Logged In" → "Never Returned"
            (everyone signed up — the question is whether they came back).
            Added "Dormant 30D+" as a separate, higher-intent win-back cohort. */}
        <div className="mb-5">
          <CustomerTierDistribution />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Stat label="Total Customers" value={data?.total ?? '—'} icon={Users} color="#39FF14" />
          <Stat
            label="Ever Returned"
            value={data?.ever_logged_in ?? '—'}
            sub={`${everPct}% of total`}
            icon={Clock}
            color="#39FF14"
          />
          <Link
            to="/dispatch/admin/boost?cohort=never"
            className="block hover:ring-2 hover:ring-[#FF9F0A] hover:ring-offset-2 hover:ring-offset-[#050003] transition-shadow"
            data-testid="never-returned-tile-launcher"
            title="Click to compose a BOOST to never-returned customers"
          >
            <Stat
              label="Never Returned →"
              value={data?.never_logged_in ?? '—'}
              sub="tap to BOOST"
              icon={Users}
              color="#FF9F0A"
            />
          </Link>
          {/* Phase 6.1.1 — One-click launcher into BOOST with this cohort pre-selected.
              Wrapping in <Link> turns the tile into a campaign launcher. */}
          <Link
            to="/dispatch/admin/boost?cohort=dormant"
            className="block hover:ring-2 hover:ring-[#FF3B30] hover:ring-offset-2 hover:ring-offset-[#050003] transition-shadow"
            data-testid="dormant-tile-launcher"
            title="Click to compose a BOOST to dormant customers"
          >
            <Stat
              label="Dormant 30D+ →"
              value={data?.dormant_30d ?? '—'}
              sub="tap to BOOST"
              icon={Clock}
              color="#FF3B30"
            />
          </Link>
          <Stat
            label={`Active Last ${days}d`}
            value={data?.active_last_n_days ?? '—'}
            icon={Clock}
            color="#39FF14"
          />
        </div>

        {/* PWA vs browser */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
          <Stat
            label="PWA Users"
            value={data?.pwa_users ?? '—'}
            sub={`${pwaPct}% installed the app`}
            icon={Smartphone}
            color="#39FF14"
          />
          <Stat
            label="Browser-Only Users"
            value={data?.browser_users ?? '—'}
            sub="never installed"
            icon={Globe}
            color="#FF3B30"
          />
          {/* Feb 2026 — Returners whose original login predates the
              is_standalone telemetry. The passive heartbeat backfills these
              within a few days of natural usage; the count should drain to
              near-zero on its own. */}
          <Stat
            label="Awaiting Classification"
            value={data?.awaiting_classification ?? '—'}
            sub="auto-clears in ~7 days"
            icon={Clock}
            color="#A3C7A8"
          />
        </div>

        {/* Filter bar */}
        <div className="border border-[#39FF14]/20 bg-[#0C0612] p-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-[200px] bg-[#050003] border border-[#39FF14]/30 px-3 py-1.5 text-white font-mono text-sm focus:outline-none focus:border-[#39FF14]"
              data-testid="login-stats-search"
            />
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { k: 'all',       label: 'ALL' },
                { k: 'never',     label: 'NEVER RETURNED' },
                { k: 'active',    label: `ACTIVE (${days}D)` },
                { k: 'dormant',   label: 'DORMANT' },
                { k: 'pwa',       label: '📱 PWA' },
                { k: 'browser',   label: '🌐 BROWSER' },
                { k: 'awaiting',  label: '⏳ AWAITING' },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setFilter(f.k)}
                  className={`px-2 py-1 border font-mono text-[10px] ${
                    filter === f.k
                      ? 'border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]'
                      : 'border-[#4B6B52] text-[#A3C7A8] hover:text-[#39FF14] hover:border-[#39FF14]'
                  }`}
                  data-testid={`filter-${f.k}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <Link
              to="/dispatch/admin/boost"
              className="px-3 py-1.5 bg-[#FF9F0A] text-[#050003] hover:bg-[#FFB341] font-mono text-xs font-bold flex items-center gap-1"
              data-testid="message-cohort"
            >
              <Send className="w-3.5 h-3.5" />
              MESSAGE COHORT
            </Link>
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#39FF14]/20 bg-[#050003]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#39FF14]/20 bg-[#0C0612]">
            <div className="font-mono text-xs text-[#4B6B52]">
              SHOWING {rows.length} {filter !== 'all' ? `· ${filter}` : ''}{search ? ` · "${search}"` : ''}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#4B6B52] font-mono text-[10px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Region</th>
                  <th className="text-left px-3 py-2">Signed Up</th>
                  <th className="text-left px-3 py-2">Last Login</th>
                  <th className="text-left px-3 py-2"># Logins</th>
                  <th className="text-left px-3 py-2">Last Device</th>
                  <th className="text-left px-3 py-2">PWA?</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-[#39FF14]/10 hover:bg-[#0C0612]/50"
                    data-testid={`row-${r.email}`}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/dispatch/admin/users?openUser=${r.id}&from=login-stats`}
                        className="font-mono text-white text-sm hover:text-[#39FF14] hover:underline"
                        data-testid={`login-stats-customer-link-${r.id}`}
                      >
                        {r.name || '—'}
                      </Link>
                      <div className="font-mono text-[#4B6B52] text-[10px]">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#A3C7A8] uppercase">
                      {r.region || '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#A3C7A8]">
                      {fmtAgo(r.created_at)} ago
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">
                      {r.last_login ? (
                        <span className={r.active_recent ? 'text-[#39FF14]' : 'text-[#A3C7A8]'}>
                          {fmtAgo(r.last_login)} ago
                        </span>
                      ) : (
                        <span className="text-[#FF3B30]">never</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-white">{r.login_count}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#A3C7A8]">
                      {r.last_ua_class ? r.last_ua_class.toUpperCase() : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {r.any_standalone ? (
                        <span className="font-mono text-[10px] text-[#39FF14]">✓ INSTALLED</span>
                      ) : r.login_count > 0 ? (
                        <span className="font-mono text-[10px] text-[#FF9F0A]">browser</span>
                      ) : (
                        <span className="font-mono text-[10px] text-[#4B6B52]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className="p-8 text-center font-mono text-[#4B6B52]">
                {'// no users match the current filter'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
